/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbWebServices } from "abweb/service/AbWebServices";
import { LocaleManager } from "abweb/service/locale/LocaleManager";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
const LOG = LogUtils.getLogger(__filename);
export class LocaleUtils {
  constructor() {
    // static class, can't create an instance
  }

  /**
   * Developers can wrap their hardcoded strings with localizeMe call to make them easier to find during
   * a subsequent localization pass.
   * @param {string} hardcodedString
   * @returns {string} hardcodedString argument value
   */
  static localizeMe(hardcodedString) {
    return hardcodedString;
  }

  /**
   * Most resource lookups should use the <lib>Resources lookup function directly.
   * However, within abweb and abreact, there are some lookups that might fail as
   * the underlying resource bundle has not yet been loaded (typically within the application
   * bootstrapping workflow, particularly localized error messages).  So use this judiciously.
   * If the underlying resource lookup takes args, use the defaultMsg function to avoid the message creation overhead
   * unless the lookup fails.
   * @param resourceLookupFn
   * @param defaultMsg
   */
  static safeResourceLookup(resourceLookupFn, defaultMsg) {
    try {
      return resourceLookupFn();
    } catch (ignore) {
      const msg = typeof defaultMsg === "string" ? defaultMsg : defaultMsg();
      LOG.error(`Resource lookup failed, defaulting to '${msg}'`);
      return msg;
    }
  }

  /**
   * Provides the language name (aka resource) for the specified languageLocale
   * from the currently loaded resource bundle (LocaleManager.languageLocale).
   * If languageLocale is not provided, defaults to current LocaleManager.languageLocale.
   **/
  static localeDisplayName(languageLocale) {
    // Note that localeDisplayName function can NOT be in LocaleManager as causes cyclic dep with I18NServices
    const langLocale = languageLocale ?? LocaleManager.languageLocale;
    if (!AbWebServices.isInitialized()) {
      return langLocale; // defensive, should never be hit, but just in case...
    }
    // As the current locale is loaded, can just use normal i18n machinery to retrieve the native language string for the current locale
    const languageKey = `locale.language.${langLocale}`;
    const translatedLanguage = AbWebServices.i18n.getMessage("abweb", languageKey);
    // On miss, getMessage returns 'abweb:${languageKey}', which is useful to the user as it lets them know the
    // resource key that they need to provide in the abweb resource bundle (more informative than just returning the langLocale).
    // Also consistent with how resource misses are handled in general.
    return translatedLanguage;
  }

  /**
   * Gets an i18n message that can have cardinality. The actual message key used is the given msgKey, with one of 3 suffixes:
   * ".zero" when cardinality is 0, ".singular" when cardinality is 1, and ".plural" when cardinality is > 1.
   * @param resourceBundleKey The resource bundle key where we will look for the message
   * @param msgKey The base message key (will have cardinality suffix appended)
   * @param cardinality The cardinality. It is passed on to the message as its first argument. If you use the props version,
   *                    those parameters are passed on to NumberUtils.formatValue to format the number, otherwise it uses
   *                    its defaults.
   * @param msgArgs Any extra message arguments, passed in after the cardinality
   */
  static messageWithCardinality(resourceBundleKey, msgKey, cardinality, ...msgArgs) {
    const cardinalityProps = typeof cardinality === "number" ? {
      cardinality
    } : cardinality;
    const formattedCardinality = NumberUtils.formatValue(cardinalityProps.cardinality, cardinalityProps.numDecimalPlaces, cardinalityProps.use1000Separator, cardinalityProps.currency);
    switch (cardinality) {
      case 0:
        return AbWebServices.i18n.getMessage(resourceBundleKey, `${msgKey}.zero`, formattedCardinality, ...msgArgs);
      case 1:
        return AbWebServices.i18n.getMessage(resourceBundleKey, `${msgKey}.singular`, formattedCardinality, ...msgArgs);
      default:
        return AbWebServices.i18n.getMessage(resourceBundleKey, `${msgKey}.plural`, formattedCardinality, ...msgArgs);
    }
  }
}
//# sourceMappingURL=LocaleUtils.js.map
